import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { OrderbookPriceLevel } from '../../types/orderbook';
import {
  formatOrderbookPrice,
  formatOrderbookSize,
  formatOrderbookTotal,
} from '../../utils/formatters';

interface DepthTableProps {
  levels: OrderbookPriceLevel[];
  side: 'asks' | 'bids';
  maxRows?: number;
  headers: {
    price: string;
    size: string;
    total: string;
  };
  highlightPrices?: number[];
  highlightTolerance?: number;
}

interface DepthRow {
  key: string;
  price: number;
  volume: number;
  totalValue: number;
  depthPercent: number;
  isBest: boolean;
}

export const OrderbookDepthTable: React.FC<DepthTableProps> = ({
  levels,
  side,
  maxRows = 20,
  headers,
  highlightPrices,
  highlightTolerance = 0,
}) => {
  const theme = useTheme();

  const processedLevels = React.useMemo<DepthRow[]>(() => {
    if (!levels || levels.length === 0) {
      return [];
    }

    const sliced = levels.slice(0, maxRows);

    const sorted = [...sliced]
      .sort((a, b) => b.price - a.price);

    const maxVolume = sorted.reduce<number>((max, current) => {
      return current.volume > max ? current.volume : max;
    }, 0);

    return sorted.map((level, index) => {
      const depthPercent = maxVolume > 0 ? level.volume / maxVolume : 0;
      const isBest =
        side === 'asks'
          ? index === sorted.length - 1
          : index === 0;

      return {
        key: `${level.price}-${index}`,
        price: level.price,
        volume: level.volume,
        totalValue: level.price * level.volume,
        depthPercent,
        isBest,
      };
    });
  }, [levels, maxRows, side]);

  const highlightMatcher = React.useMemo(() => {
    if (!Array.isArray(highlightPrices) || highlightPrices.length === 0) {
      return () => false;
    }

    const normalizedSet = new Set<string>();
    const numericValues: number[] = [];

    highlightPrices.forEach((price) => {
      if (typeof price === 'number' && Number.isFinite(price)) {
        normalizedSet.add(price.toFixed(8));
        numericValues.push(price);
      }
    });

    if (normalizedSet.size === 0) {
      return () => false;
    }

    const tolerance = Math.max(highlightTolerance, 0);

    return (price: number) => {
      if (!Number.isFinite(price)) {
        return false;
      }

      if (normalizedSet.has(price.toFixed(8))) {
        return true;
      }

      if (tolerance > 0) {
        return numericValues.some((candidate) => Math.abs(candidate - price) <= tolerance);
      }

      return false;
    };
  }, [highlightPrices, highlightTolerance]);

  const depthColor = side === 'asks' ? theme.palette.error.main : theme.palette.success.main;
  const priceColor = side === 'asks' ? theme.palette.error.light : theme.palette.success.light;
  const backgroundAlignmentProperty = side === 'asks' ? 'right' : 'left';

  if (!processedLevels.length) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          columnGap: 2,
          px: 1.5,
          py: 0.5,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
          {headers.price}
        </Typography>
        <Typography variant="caption" align="right" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
          {headers.size}
        </Typography>
        <Typography variant="caption" align="right" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
          {headers.total}
        </Typography>
      </Box>

      {processedLevels.map((row) => {
        const depthWidth = Math.min(Math.max(row.depthPercent, 0), 1) * 100;
        const isHighlighted = highlightMatcher(row.price);
        const overlayOpacity = isHighlighted ? 0.5 : 0.12;
        const borderColor = isHighlighted ? alpha(depthColor, 0.7) : 'transparent';

        return (
          <Box
            key={row.key}
            sx={{
              position: 'relative',
              px: 1.5,
              py: 0.75,
              borderRadius: 1,
              overflow: 'hidden',
              fontVariantNumeric: 'tabular-nums',
              border: `1px solid ${borderColor}`,
              transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
              boxShadow: isHighlighted ? `0 0 16px ${alpha(depthColor, 0.35)}` : 'none',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                bottom: 0,
                [backgroundAlignmentProperty]: 0,
                width: `${depthWidth}%`,
                background: alpha(depthColor, overlayOpacity),
              },
            }}
          >
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                columnGap: 2,
                alignItems: 'center',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: row.isBest || isHighlighted ? 700 : 500,
                  color: isHighlighted ? theme.palette.common.white : priceColor,
                  textShadow: isHighlighted ? `0 0 6px ${alpha(depthColor, 0.7)}` : 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatOrderbookPrice(row.price)}
              </Typography>
              <Typography
                variant="body2"
                align="right"
                sx={{ color: 'text.primary', whiteSpace: 'nowrap' }}
              >
                {formatOrderbookSize(row.volume)}
              </Typography>
              <Typography
                variant="body2"
                align="right"
                sx={{
                  color: isHighlighted ? 'text.primary' : 'text.secondary',
                  whiteSpace: 'nowrap',
                  fontWeight: isHighlighted ? 500 : 400,
                }}
              >
                {formatOrderbookTotal(row.totalValue)}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default OrderbookDepthTable;
